import { makeStyles } from '@material-ui/core/styles'
import { colors } from 'src/components/app-ui'

const inputBase = {
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  padding: '10px 12px',
  border: '1px solid rgba(145,158,171,.32)',
  borderRadius: '8px',
  outline: 'none',
  background: '#FFFFFF',
  fontFamily: 'inherit',
  fontSize: '14px',
  color: colors.navy,
  '&:focus': {
    borderColor: colors.primary,
  },
}

const menuItem = {
  width: '100%',
  height: 36,
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '0 10px',
  border: 0,
  borderRadius: '8px',
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: 500,
  color: colors.navy,
  '&:hover': {
    background: colors.hover,
  },
}

const GRID = 'minmax(0,1.2fr) minmax(0,1.6fr) minmax(0,1.2fr) 36px'

const useStyles = makeStyles({
  root: {
    maxWidth: 960,
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    color: colors.navy,
  },

  // Steps card
  steps: {
    maxWidth: 640,
    display: 'flex',
    flexDirection: 'column',
    padding: '22px 24px',
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    background: '#FFFFFF',
  },
  step: {
    display: 'flex',
    gap: '14px',
  },
  rail: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  mark: {
    width: 24,
    height: 24,
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    fontSize: '12px',
    fontWeight: 600,
    color: '#FFFFFF',
  },
  line: {
    flex: 1,
    width: 1,
    minHeight: 16,
    margin: '6px 0',
    background: colors.border,
  },
  stepBody: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  stepTitle: {
    padding: 0,
    border: 0,
    background: 'transparent',
    textAlign: 'left',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '24px',
  },
  stepText: {
    fontSize: '13px',
    lineHeight: 1.5,
    color: colors.muted,
    '& a': {
      color: colors.primary,
    },
  },
  cname: {
    maxWidth: 460,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 8px 8px 12px',
    borderRadius: '8px',
    background: colors.hover,
  },
  cnameLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: colors.muted,
  },
  cnameValue: {
    flex: 1,
    fontFamily: 'ui-monospace, Menlo, monospace',
    fontSize: '13px',
    color: colors.navy,
  },
  copy: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 10px',
    border: '1px solid rgba(1,95,251,.4)',
    borderRadius: '6px',
    background: '#FFFFFF',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '12px',
    fontWeight: 500,
    color: colors.primary,
  },
  primary: {
    alignSelf: 'flex-start',
    padding: '9px 16px',
    border: 0,
    borderRadius: '8px',
    background: colors.primary,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: '#FFFFFF',
    '&:hover:not(:disabled)': {
      background: colors.primaryDark,
    },
    '&:disabled': {
      background: colors.disabled,
      cursor: 'default',
    },
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.navy,
  },
  input: inputBase,
  selectWrap: {
    position: 'relative',
  },
  select: {
    ...inputBase,
    appearance: 'none',
    WebkitAppearance: 'none',
    paddingRight: 36,
    cursor: 'pointer',
  },
  chevron: {
    position: 'absolute',
    top: '50%',
    right: 12,
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    pointerEvents: 'none',
    color: colors.muted,
  },
  hint: {
    fontSize: '12px',
    fontWeight: 400,
    color: colors.muted,
  },
  hintMuted: {
    fontSize: '12px',
    fontWeight: 400,
    color: colors.subtle,
  },

  // Domains table
  table: {
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    background: '#FFFFFF',
  },
  tableTitle: {
    padding: '16px 20px',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: '15px',
    fontWeight: 600,
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: GRID,
    gap: '12px',
    padding: '10px 20px',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    color: colors.subtle,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: GRID,
    gap: '12px',
    alignItems: 'center',
    padding: '12px 20px',
    borderBottom: '1px solid #F1F3F5',
    fontSize: '14px',
    '&:hover': {
      background: '#F7F7F7',
    },
  },
  domain: {
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  target: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    '& > span:last-child': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  status: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
  },
  more: {
    display: 'inline-flex',
    padding: 4,
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    color: colors.muted,
    '&:hover': {
      background: '#EEF1F5',
      color: colors.navy,
    },
  },
  menu: {
    minWidth: 170,
    padding: '6px',
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    boxShadow:
      '0 20px 40px -4px rgba(145,158,171,.28), 0 0 2px rgba(145,158,171,.2)',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  menuItem,
  menuDanger: {
    ...menuItem,
    color: '#C9383B',
    '&:hover': {
      background: '#FDECEC',
    },
  },
  menuDivider: {
    height: 1,
    margin: '4px 2px',
    background: '#F1F3F5',
  },
  empty: {
    padding: '32px 20px',
    textAlign: 'center',
    fontSize: '13px',
    color: colors.muted,
  },
  pager: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '20px',
    padding: '10px 20px',
    fontSize: '13px',
    color: colors.muted,
  },
  perPage: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  perSelect: {
    appearance: 'none',
    WebkitAppearance: 'none',
    padding: '4px 20px 4px 4px',
    border: 0,
    outline: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    color: colors.navy,
  },
  pageButton: {
    display: 'inline-flex',
    padding: 4,
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    fontFamily: 'inherit',
  },

  // Dialogs
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 1300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: 'rgba(0,0,0,.48)',
  },
  dialog: {
    width: '100%',
    maxWidth: 460,
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    padding: '22px 24px',
    borderRadius: '16px',
    background: '#FFFFFF',
    boxShadow: '0 20px 40px -4px rgba(145,158,171,.24)',
    fontFamily: "'Inter', system-ui, sans-serif",
    color: colors.navy,
  },
  dialogHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dialogTitle: {
    fontSize: '18px',
    fontWeight: 600,
  },
  dialogSub: {
    marginTop: 2,
    fontSize: '13px',
    color: colors.muted,
  },
  dialogClose: {
    display: 'flex',
    padding: 4,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    color: colors.muted,
  },
  dialogActions: {
    marginTop: 4,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  textButton: {
    padding: '9px 14px',
    border: 0,
    borderRadius: '8px',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.muted,
  },
  dangerButton: {
    padding: '9px 16px',
    border: 0,
    borderRadius: '8px',
    background: colors.error,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: '#FFFFFF',
    '&:hover': {
      background: '#C9383B',
    },
  },
})

export default useStyles
